<template>
  <div class="minister-dashboard">
    <PageLoader :storage="appLoading" />
    <ServerError v-if="serverError" />

    <div class="minister-dashboard__grid-wrapper">
      <!-- Left side: contains Header, Overview, Stats, and Panels -->
      <div class="minister-dashboard__main-content">
      
      <!-- Situation Overview Bar -->
      <div class="situation-overview-bar">
        <div class="overview-bar-row">
          <div>
            <div class="overview-header">{{ dashboardTitle }}</div>
            <div class="overview-body">
              <span class="overview-date">{{ previousDayLabel }}</span>
              <span class="overview-badge">{{ dateBadgeLabel }}</span>
            </div>
          </div>

          <div class="overview-filter">
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="displayDate"
                  label="Select Date"
                  append-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  hide-details
                  class="date-filter-input"
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                :value="displayDate"
                :max="maxDisplayDate"
                @change="onDateChange"
              ></v-date-picker>
            </v-menu>
          </div>
        </div>
      </div>

      <!-- Stat Cards Row -->
      <div class="minister-dashboard__stats">
        <article class="stat-card">
          <div class="stat-card__label">CALLS RECEIVED</div>
          <div class="stat-card__value">{{ formatNumber(summary.callersCount) }}</div>
        </article>

        <article class="stat-card stat-card--clickable" @click="onCardClick({ label: 'Incidents Reported' })">
          <div class="stat-card__label">INCIDENTS REPORTED</div>
          <div class="stat-card__value">{{ formatNumber(summary.incidentsReported) }}</div>
        </article>

        <article
          class="stat-card stat-card--clickable"
          @click="onCardClick({ label: 'Resolved' })"
        >
          <div class="stat-card__label">RESOLVED</div>
          <div class="stat-card__value">{{ formatNumber(summary.incidentsResolved) }}</div>
        </article>

        <article
          class="stat-card stat-card--clickable"
          @click="onCardClick({ label: 'Pending' })"
        >
          <div class="stat-card__label">PENDING</div>
          <div class="stat-card__value text-orange">{{ formatNumber(summary.incidentsPending) }}</div>
        </article>

        <article class="stat-card">
          <div class="stat-card__label">RESOLUTION RATE</div>
          <div class="d-flex align-center justify-space-between mt-2">
            <div class="stat-card__value">{{ summary.resolutionRate }}%</div>
            <v-progress-circular
              :value="summary.resolutionRate"
              size="28"
              width="3.5"
              color="#2e5a38"
              background-color="#edf2eb"
            ></v-progress-circular>
          </div>
        </article>
      </div>

      <!-- Left Column (2.2 / 3.2 width) -->
      <div class="left-column">
        <!-- Row 1: Status, Ageing, Divisions -->
        <div class="left-column__row">
          <!-- Today's Casualty & Damage Summary (for HWC) OR Snake Summary (for SARPA) -->
          <section class="panel panel--damage-summary grid-span-2">
            <div class="panel__head">
              <div class="panel__title">
                {{ activeTab === 'sarpa' ? "Snake Rescue & Incident Summary" : "Casualty & Damage Summary" }}
              </div>
              <div class="panel__subtitle">
                {{ activeTab === 'sarpa' ? "Reported snake sightings, rescues" : "Reported casualties & damages" }}
              </div>
            </div>
            <div class="panel__content d-flex flex-column justify-space-around flex-grow-1">
              
              <!-- SARPA Snake Summary Grid -->
              <div v-if="activeTab === 'sarpa'" class="damage-grid">
                <div class="damage-card">
                  <div class="damage-card__icon"><v-icon small color="#2e5a38">mdi-snake</v-icon></div>
                  <div class="damage-card__info">
                    <span class="damage-card__label">Snake Reports</span>
                    <span class="damage-card__value">{{ (summary.snakeSummary && summary.snakeSummary.totalCalls !== undefined) ? summary.snakeSummary.totalCalls : summary.incidentsReported || 0 }}</span>
                  </div>
                </div>

                <div class="damage-card">
                  <div class="damage-card__icon"><v-icon small color="#c62828">mdi-flash</v-icon></div>
                  <div class="damage-card__info">
                    <span class="damage-card__label">Venomous</span>
                    <span class="damage-card__value text-red-bold">{{ summary.snakeSummary ? summary.snakeSummary.venomousCount : 0 }}</span>
                  </div>
                </div>

                <div class="damage-card">
                  <div class="damage-card__icon"><v-icon small color="#2e5a38">mdi-leaf</v-icon></div>
                  <div class="damage-card__info">
                    <span class="damage-card__label">Non-Venomous</span>
                    <span class="damage-card__value">{{ summary.snakeSummary ? summary.snakeSummary.nonVenomousCount : 0 }}</span>
                  </div>
                </div>

                <div class="damage-card">
                  <div class="damage-card__icon"><v-icon small color="#718171">mdi-help-circle-outline</v-icon></div>
                  <div class="damage-card__info">
                    <span class="damage-card__label">Unidentified</span>
                    <span class="damage-card__value">{{ summary.snakeSummary ? summary.snakeSummary.unidentifiedCount : 0 }}</span>
                  </div>
                </div>

                <div class="damage-card">
                  <div class="damage-card__icon"><v-icon small color="#2e5a38">mdi-target</v-icon></div>
                  <div class="damage-card__info">
                    <span class="damage-card__label">Snakes Rescued</span>
                    <span class="damage-card__value text-amber-bold">{{ summary.snakeSummary ? summary.snakeSummary.rescuedCount : 0 }}</span>
                  </div>
                </div>

                <div class="damage-card">
                  <div class="damage-card__icon"><v-icon small color="#2e5a38">mdi-pine-tree</v-icon></div>
                  <div class="damage-card__info">
                    <span class="damage-card__label">Snake Released</span>
                    <span class="damage-card__value">{{ summary.snakeSummary ? summary.snakeSummary.releasedCount : 0 }}</span>
                  </div>
                </div>

                <div class="damage-card">
                  <div class="damage-card__icon"><v-icon small color="#d84315">mdi-clock-outline</v-icon></div>
                  <div class="damage-card__info">
                    <span class="damage-card__label">Pending Rescues</span>
                    <span class="damage-card__value text-amber-bold">{{ summary.incidentsPending || 0 }}</span>
                  </div>
                </div>

                <div class="damage-card">
                  <div class="damage-card__icon"><v-icon small color="#2e5a38">mdi-shield-account</v-icon></div>
                  <div class="damage-card__info">
                    <span class="damage-card__label">Rescuers Active</span>
                    <span class="damage-card__value">{{ summary.snakeSummary ? summary.snakeSummary.rrtDispatched : 0 }}</span>
                  </div>
                </div>
              </div>

              <!-- HWC Damage Grid -->
              <div v-else class="damage-grid">
                <div class="damage-card">
                  <div class="damage-card__icon damage-card__icon--danger"><v-icon small color="#c62828">mdi-account-off</v-icon></div>
                  <div class="damage-card__info">
                    <span class="damage-card__label">Human Deaths</span>
                    <span class="damage-card__value text-red-bold">{{ (summary.damageSummary && summary.damageSummary.humanDeaths) || 0 }}</span>
                  </div>
                </div>

                <div class="damage-card">
                  <div class="damage-card__icon damage-card__icon--danger"><v-icon small color="#c62828">mdi-paw-off</v-icon></div>
                  <div class="damage-card__info">
                    <span class="damage-card__label">Animal Deaths</span>
                    <span class="damage-card__value text-red-bold">{{ (summary.damageSummary && summary.damageSummary.animalDeaths) || 0 }}</span>
                  </div>
                </div>

                <div class="damage-card">
                  <div class="damage-card__icon damage-card__icon--warn"><v-icon small color="#d84315">mdi-account-injury</v-icon></div>
                  <div class="damage-card__info">
                    <span class="damage-card__label">Human Injury</span>
                    <span class="damage-card__value text-amber-bold">{{ (summary.damageSummary && summary.damageSummary.humanInjuries) || 0 }}</span>
                  </div>
                </div>

                <div class="damage-card">
                  <div class="damage-card__icon damage-card__icon--warn"><v-icon small color="#d84315">mdi-bandage</v-icon></div>
                  <div class="damage-card__info">
                    <span class="damage-card__label">Animal Injury</span>
                    <span class="damage-card__value text-amber-bold">{{ (summary.damageSummary && summary.damageSummary.animalInjuries) || 0 }}</span>
                  </div>
                </div>

                <div class="damage-card">
                  <div class="damage-card__icon damage-card__icon--loss"><v-icon small color="#2e5a38">mdi-cow</v-icon></div>
                  <div class="damage-card__info">
                    <span class="damage-card__label">Livestock Loss</span>
                    <span class="damage-card__value">{{ (summary.damageSummary && summary.damageSummary.livestockLoss) || 0 }}</span>
                  </div>
                </div>

                <div class="damage-card">
                  <div class="damage-card__icon damage-card__icon--loss"><v-icon small color="#2e5a38">mdi-leaf</v-icon></div>
                  <div class="damage-card__info">
                    <span class="damage-card__label">Crop Damage</span>
                    <span class="damage-card__value">{{ (summary.damageSummary && summary.damageSummary.cropDamage) || 0 }}</span>
                  </div>
                </div>

                <div class="damage-card">
                  <div class="damage-card__icon damage-card__icon--loss"><v-icon small color="#2e5a38">mdi-home-alert</v-icon></div>
                  <div class="damage-card__info">
                    <span class="damage-card__label">Property Damage</span>
                    <span class="damage-card__value">{{ (summary.damageSummary && summary.damageSummary.propertyDamage) || 0 }}</span>
                  </div>
                </div>

                <div class="damage-card">
                  <div class="damage-card__icon damage-card__icon--good"><v-icon small color="#2e5a38">mdi-target</v-icon></div>
                  <div class="damage-card__info">
                    <span class="damage-card__label">Animal Captured</span>
                    <span class="damage-card__value">{{ (summary.damageSummary && summary.damageSummary.animalsCaptured) || 0 }}</span>
                  </div>
                </div>

                <!-- Incidents assessed as causing no casualty or damage at all -->
                <div
                  class="damage-card damage-card--clear"
                  title="Incidents assessed as causing no casualty or damage of any kind"
                >
                  <div class="damage-card__icon damage-card__icon--good"><v-icon small color="#2e7d32">mdi-shield-check-outline</v-icon></div>
                  <div class="damage-card__info">
                    <span class="damage-card__label">No Damage Reported</span>
                    <span class="damage-card__value text-green-bold">{{ (summary.damageSummary && summary.damageSummary.noDamage) || 0 }}</span>
                    <span
                      v-if="summary.damageSummary && summary.damageSummary.pendingAssessment"
                      class="damage-card__note"
                      >{{ summary.damageSummary.pendingAssessment }} awaiting
                      assessment</span
                    >
                    <span v-else class="damage-card__note"
                      >of {{ formatNumber(summary.incidentsReported) }} incidents</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Top Divisions (HWC) OR Top Reported Snake Species (SARPA) -->
          <section class="panel panel--divisions-list">
            <div class="panel__head">
              <div class="panel__title">
                {{ activeTab === 'sarpa' ? "Top Reported Snake Species" : "Top Divisions by Incidents" }}
              </div>
              <div class="panel__subtitle">
                {{ activeTab === 'sarpa' ? "Most frequently reported & rescued snakes today" : "Divisions with highest reported cases" }}
              </div>
            </div>
            <div class="panel__content d-flex flex-column justify-space-between flex-grow-1">
              <!-- SARPA View: Top Snake Species -->
              <div v-if="activeTab === 'sarpa'">
                <div v-if="summary.animalWiseIncidents && summary.animalWiseIncidents.length > 0" class="list-container">
                  <div
                    v-for="(item, index) in summary.animalWiseIncidents.slice(0, 5)"
                    :key="item.name"
                    class="division-item d-flex align-center mb-2"
                  >
                    <span class="division-label text-capitalize">{{ item.name }}</span>
                    <div class="division-progress-wrap flex-grow-1 mx-3">
                      <v-progress-linear
                        :value="getAnimalPercentage(item.count)"
                        height="8"
                        rounded
                        :color="getAnimalColor(index)"
                        background-color="#edf2eb"
                      ></v-progress-linear>
                    </div>
                    <span class="division-value">{{ item.count }}</span>
                  </div>
                </div>
                <div v-else class="text-center py-5 text-caption text-secondary">
                  No snake species reported today
                </div>
              </div>

              <!-- HWC View: Top Divisions -->
              <div v-else>
                <div v-if="summary.divisionWiseIncidents && summary.divisionWiseIncidents.length > 0" class="list-container">
                  <div
                    v-for="item in summary.divisionWiseIncidents.slice(0, 5)"
                    :key="item.name"
                    class="division-item d-flex align-center mb-2"
                  >
                    <span class="division-label">{{ item.name }}</span>
                    <div class="division-progress-wrap flex-grow-1 mx-3">
                      <v-progress-linear
                        :value="getDivisionPercentage(item.count)"
                        height="8"
                        rounded
                        color="#8fae95"
                        background-color="#edf2eb"
                      ></v-progress-linear>
                    </div>
                    <span class="division-value">{{ item.count }}</span>
                  </div>
                </div>
                <div v-else class="text-center py-5 text-caption text-secondary">
                  No division incidents reported
                </div>
              </div>
            </div>
          </section>
        </div>

        <!-- Row 2: Category, Animal, Performance -->
        <div class="left-column__row">
          <!-- Category-wise Calls -->
          <section class="panel panel--categories-list">
            <div class="panel__head">
              <div class="panel__title">Category-wise Calls</div>
              <div class="panel__subtitle">Call volume by conflict scenario</div>
            </div>
            <div class="panel__content d-flex flex-column justify-space-between flex-grow-1">
              <div v-if="summary.categoryWiseCalls && summary.categoryWiseCalls.length > 0" class="list-container">
                <div
                  v-for="(item, index) in summary.categoryWiseCalls.slice(0, 4)"
                  :key="item.name"
                  class="category-item d-flex align-center mb-2"
                >
                  <span class="category-label" :title="item.name">{{ item.name }}</span>
                  <div class="category-progress-wrap flex-grow-1 mx-3">
                    <v-progress-linear
                      :value="getCategoryPercentage(item.count)"
                      height="8"
                      rounded
                      :color="getCategoryColor(index)"
                      background-color="#edf2eb"
                    ></v-progress-linear>
                  </div>
                  <span class="category-value">{{ item.count }}</span>
                </div>
              </div>
              <div v-else class="text-center py-5 text-caption text-secondary">
                No conflict calls reported
              </div>
            </div>
          </section>

          <!-- Animal-wise Incidents -->
          <section class="panel panel--animals-list">
            <div class="panel__head">
              <div class="panel__title">Animal-wise Incidents</div>
              <div class="panel__subtitle">Incidents grouped by animal</div>
            </div>
            <div class="panel__content d-flex flex-column justify-space-between flex-grow-1">
              <div v-if="summary.animalWiseIncidents && summary.animalWiseIncidents.length > 0" class="list-container">
                <div
                  v-for="(item, index) in summary.animalWiseIncidents.slice(0, 4)"
                  :key="item.name"
                  class="animal-item d-flex align-center mb-2"
                >
                  <span class="animal-label">{{ item.name }}</span>
                  <div class="animal-progress-wrap flex-grow-1 mx-3">
                    <v-progress-linear
                      :value="getAnimalPercentage(item.count)"
                      height="8"
                      rounded
                      :color="getAnimalColor(index)"
                      background-color="#edf2eb"
                    ></v-progress-linear>
                  </div>
                  <span class="animal-value">{{ item.count }}</span>
                </div>
              </div>
              <div v-else class="text-center py-5 text-caption text-secondary">
                No animal incidents reported
              </div>
            </div>
          </section>

          <!-- Response Performance -->
          <section class="panel panel--performance">
            <div class="panel__head">
              <div class="panel__title">Response Performance</div>
              <div class="panel__subtitle">Department response times & SLAs</div>
            </div>
            <div class="panel__content d-flex flex-column justify-space-around flex-grow-1">
              <div class="perf-list">
                <div class="perf-row d-flex align-center justify-space-between py-2">
                  <span class="perf-label">Avg. response</span>
                  <span class="perf-value">{{ summary.responsePerformance.avgResponse }}</span>
                </div>

                <div class="perf-row d-flex align-center justify-space-between py-2">
                  <span class="perf-label">Longest pending</span>
                  <span class="perf-value">{{ summary.responsePerformance.longestPending }}</span>
                </div>

                <div class="perf-row d-flex align-center justify-space-between py-2">
                  <span class="perf-label">Escalated cases</span>
                  <span class="perf-value text-red-bold">{{ summary.responsePerformance.escalatedCount || 0 }}</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div> <!-- Closes minister-dashboard__main-content -->

    <!-- Right side: Map Card goes all the way to the top -->
    <div class="minister-dashboard__side-content">
      <!-- Right Column (1 / 3.2 width) -->
      <div class="right-column">
        <section class="panel panel--map flex-grow-1 d-flex flex-column">
          <div class="panel__head">
            <div class="panel__title">District-wise Incident Heat Map</div>
            <div class="panel__subtitle">Incident distribution across Kerala districts</div>
          </div>

          <div class="district-map mt-2" style="position: relative;">
            <l-map
              ref="districtMap"
              :zoom="mapZoom"
              :center="mapCenter"
              :options="{ zoomControl: true, attributionControl: false, dragging: true, doubleClickZoom: false, scrollWheelZoom: false, zoomSnap: 0.25, zoomDelta: 0.25 }"
              class="district-map__canvas"
              style="height: 100%; width: 100%;"
              @ready="fitDistrictMap"
            >
              <l-geo-json
                v-if="districtGeoJson"
                :geojson="districtGeoJson"
                :options-style="districtStyle"
              />

              <l-marker
                v-for="marker in districtMarkers"
                :key="marker.name"
                :lat-lng="marker.latlng"
                :icon="marker.icon"
              />
            </l-map>
          </div>

          <!-- Map Legend -->
          <div class="map-legend mt-3">
            <div class="map-legend__title">Incident Density</div>
            <div class="map-legend__grid">
              <div class="map-legend__item">
                <span class="legend-dot" style="background-color: #f4f8f3; border: 1px solid #cfdcd0;"></span>
                <span class="legend-text">Zero (0)</span>
              </div>
              <div class="map-legend__item">
                <span class="legend-dot" style="background-color: #aed581;"></span>
                <span class="legend-text">Low (1-5)</span>
              </div>
              <div class="map-legend__item">
                <span class="legend-dot" style="background-color: #ffb74d;"></span>
                <span class="legend-text">Medium (6-15)</span>
              </div>
              <div class="map-legend__item">
                <span class="legend-dot" style="background-color: #ff8a65;"></span>
                <span class="legend-text">High (16-30)</span>
              </div>
              <div class="map-legend__item">
                <span class="legend-dot" style="background-color: #e57373;"></span>
                <span class="legend-text">Very High (31+)</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div> <!-- Closes minister-dashboard__side-content -->
    </div> <!-- Closes minister-dashboard__grid-wrapper -->
  </div>
</template>

<script>
import axios from "axios";
import L from "leaflet";
import { LMap, LGeoJson, LMarker } from "vue2-leaflet";

/**
 * A reporting day runs 10:00 IST to 09:59:59 IST the next morning, and the API
 * labels it by the date it ENDS on: `date=D` means D-1 10:00 -> D 09:59.
 * IST has no DST, so a fixed +05:30 offset is safe on any client clock.
 */
const IST_OFFSET = "+05:30";
const IST_OFFSET_MINUTES = 5 * 60 + 30;
const DAY_OPENS_AT_HOUR = 10;

function istDateStr(date) {
  return new Date(date).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

function istHour(date) {
  const shifted = new Date(new Date(date).getTime() + IST_OFFSET_MINUTES * 60000);
  return shifted.getUTCHours();
}

function shiftIstDays(istStr, days) {
  const d = new Date(`${istStr}T00:00:00.000${IST_OFFSET}`);
  d.setUTCDate(d.getUTCDate() + days);
  return istDateStr(d);
}

/**
 * The `date` value of the most recently CLOSED reporting day. Today's window
 * ends at 09:59, so it is only complete once 10:00 has passed; before that the
 * newest closed window is yesterday's.
 */
function latestClosedReportingDate(now = new Date()) {
  const nowIst = istDateStr(now);
  return istHour(now) >= DAY_OPENS_AT_HOUR ? nowIst : shiftIstDays(nowIst, -1);
}

function prettyIstDate(istStr) {
  return new Date(`${istStr}T00:00:00.000${IST_OFFSET}`).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    timeZone: "Asia/Kolkata",
  });
}

export default {
  props: {
    analyticType: {
      type: String,
      default: "all",
    },
  },
  components: {
    LMap,
    LGeoJson,
    LMarker,
  },
  data() {
    // Land on the most recently closed reporting day: after 10:00 IST that is
    // yesterday 10:00 -> today 09:59, before 10:00 it is the day before that.
    const latestDate = latestClosedReportingDate();

    return {
      appLoading: false,
      hasLoadedOnce: false,
      serverError: false,
      refreshHandle: null,
      // API parameter: the morning the reporting window CLOSES.
      selectedDate: latestDate,
      dateMenu: false,
      onWindowResize: null,
      mapCenter: [10.8505, 76.2711],
      mapZoom: 7.25,
      districtGeoJson: null,
      summary: {
        callersCount: 0,
        incidentsReported: 0,
        incidentsResolved: 0,
        incidentsPending: 0,
        resolutionRate: 0,
        categoryWiseCalls: [],
        animalWiseIncidents: [],
        districtWiseIncidents: [],
        divisionWiseIncidents: [],
        damageSummary: {
          humanDeaths: 0,
          animalDeaths: 0,
          humanInjuries: 0,
          animalInjuries: 0,
          livestockLoss: 0,
          cropDamage: 0,
          propertyDamage: 0,
          animalsCaptured: 0,
          totalDamageCases: 0,
        },
        snakeSummary: {
          totalCalls: 0,
          venomousCount: 0,
          nonVenomousCount: 0,
          rescuedCount: 0,
          releasedCount: 0,
          humanBitesCount: 0,
          animalBitesCount: 0,
          rrtDispatched: 0,
        },
        ageing: {
          under2h: 0,
          from2to6h: 0,
          from6to12h: 0,
          over12h: 0,
          rrt: 0,
          critical: 0,
        },
        responsePerformance: {
          avgResponse: "N/A",
          longestPending: "N/A",
          escalatedCount: 0,
        },
      },
      districtChartMap: {},
      statusChartOptions: {
        chart: {
          type: "pie",
          backgroundColor: "transparent",
          height: 135,
          spacing: [0, 0, 0, 0],
        },
        title: {
          text: "",
          align: "center",
          verticalAlign: "middle",
          y: 10,
          useHTML: true,
          style: {
            fontFamily: "Poppins, sans-serif",
          },
        },
        tooltip: {
          pointFormat: "<b>{point.y}</b> ({point.percentage:.1f}%)",
        },
        credits: { enabled: false },
        exporting: { enabled: false },
        plotOptions: {
          pie: {
            innerSize: "75%",
            borderWidth: 0,
            dataLabels: { enabled: false },
            showInLegend: false,
          },
        },
        series: [
          {
            name: "Status",
            colorByPoint: true,
            data: [],
          },
        ],
      },
    };
  },
  computed: {
    effectiveType() {
      return (
        this.analyticType ||
        (this.$route && this.$route.meta && this.$route.meta.analyticType) ||
        (this.$route && this.$route.query && this.$route.query.type) ||
        "all"
      ).toLowerCase();
    },
    activeTab() {
      if (this.effectiveType === "sarpa") return "sarpa";
      return "wildwatch";
    },
    dashboardTitle() {
      if (this.effectiveType === "hwc") return "HWC SITUATION OVERVIEW";
      if (this.effectiveType === "sarpa") return "SARPA SITUATION OVERVIEW";
      return "SITUATION OVERVIEW";
    },
    // The window spans two calendar days; the report is identified by the day it
    // OPENS (10:00), which is where the bulk of the data comes from. The API is
    // keyed by the closing day, so the two differ by one.
    displayDate() {
      return this.selectedDate ? shiftIstDays(this.selectedDate, -1) : "";
    },
    maxDisplayDate() {
      return shiftIstDays(latestClosedReportingDate(), -1);
    },
    previousDayLabel() {
      if (!this.displayDate) return "Previous day";
      return new Date(`${this.displayDate}T00:00:00.000${IST_OFFSET}`).toLocaleDateString(
        "en-IN",
        { day: "2-digit", month: "long", year: "numeric", timeZone: "Asia/Kolkata" }
      );
    },
    dateBadgeLabel() {
      // Derived from the selected day so the badge cannot drift from the window
      // actually being queried.
      if (!this.selectedDate) return "10:00 AM – 09:59 AM";
      const opensOn = shiftIstDays(this.selectedDate, -1);
      return `${prettyIstDate(opensOn)} 10:00 AM – ${prettyIstDate(
        this.selectedDate
      )} 09:59 AM`;
    },
    districtMarkers() {
      if (!this.districtGeoJson || !this.districtGeoJson.features) {
        return [];
      }

      // Coordinates offset to prevent label overlaps in narrow/clustered southern districts of Kerala
      const DISTRICT_OFFSETS = {
        alappuzha: [0.02, -0.20],
        ernakulam: [0.06, -0.06],
        kottayam: [0.04, 0.03],
        idukki: [0.08, 0.18],
        pathanamthitta: [-0.08, 0.08],
        kollam: [-0.08, -0.05],
      };

      return this.districtGeoJson.features
        .map((feature) => {
          const districtName = this.getDistrictName(feature);
          const lookupKey = districtName ? districtName.toLowerCase().trim() : "";
          const count = this.districtChartMap[lookupKey] || 0;
          if (!districtName || count === 0) {
            return null; // hide districts with 0 reports to avoid congestion
          }

          const bounds = L.geoJSON(feature).getBounds();
          if (!bounds.isValid()) {
            return null;
          }

          let latlng = bounds.getCenter();
          const key = districtName.toLowerCase().trim();
          if (DISTRICT_OFFSETS[key]) {
            latlng = L.latLng(
              latlng.lat + DISTRICT_OFFSETS[key][0],
              latlng.lng + DISTRICT_OFFSETS[key][1]
            );
          }

          const abbrev = this.getDistrictAbbrev(districtName);

          return {
            name: districtName,
            latlng,
            icon: L.divIcon({
              className: "district-count-icon",
              html: `
                <div class="district-marker-label">
                  <span class="district-marker-label__name">${abbrev}</span>
                  <span class="district-marker-label__count">${count}</span>
                </div>
              `,
              iconAnchor: [0, 0],
            }),
          };
        })
        .filter(Boolean);
    },
  },
  mounted() {
    this.loadDashboard({ showLoader: true });
    this.refreshHandle = setInterval(() => this.loadDashboard({ showLoader: false }), 3000);
    // The map width changes at each breakpoint; Leaflet needs telling or it
    // keeps the old canvas size and renders blank / grey strips.
    this.onWindowResize = () => this.fitDistrictMap();
    window.addEventListener("resize", this.onWindowResize);
  },
  beforeDestroy() {
    if (this.refreshHandle) {
      clearInterval(this.refreshHandle);
    }
    if (this.onWindowResize) {
      window.removeEventListener("resize", this.onWindowResize);
    }
  },
  methods: {
    formatNumber(value) {
      return Number(value || 0).toLocaleString("en-IN");
    },
    onCardClick(card) {
      const query = {
        date: this.selectedDate,
        status: "all",
        type: this.activeTab === "sarpa" || this.analyticType === "sarpa" ? "sarpa" : "wildwatch",
      };

      if (card.label === "Resolved") {
        query.status = "resolved";
      } else if (card.label === "Pending") {
        query.status = "pending";
      }

      this.$router.push({ name: "adminCommonIncidents", query });
    },
    districtStyle(feature) {
      const districtName = this.getDistrictName(feature);
      const lookupKey = districtName ? districtName.toLowerCase().trim() : "";
      const count = this.districtChartMap[lookupKey] || 0;

      if (count === 0) {
        return {
          color: "#000000",
          weight: 1,
          fillColor: "#f4f8f3",
          fillOpacity: 0.15,
        };
      }

      let fillColor = "#aed581"; // Low
      if (count >= 31) {
        fillColor = "#e57373"; // Very High
      } else if (count >= 16) {
        fillColor = "#ff8a65"; // High
      } else if (count >= 6) {
        fillColor = "#ffb74d"; // Medium
      }

      return {
        color: "#000000",
        weight: 2,
        fillColor: fillColor,
        fillOpacity: 0.85,
      };
    },
    getDistrictName(feature) {
      if (!feature || !feature.properties) {
        return "";
      }

      return (
        feature.properties.DISTRICT ||
        feature.properties.circleName ||
        feature.properties.name ||
        feature.properties.NAME ||
        feature.properties.district ||
        ""
      );
    },
    getDistrictAbbrev(name) {
      const lookup = {
        kasaragod: "KSG",
        kannur: "KNR",
        wayanad: "WYD",
        kozhikode: "KZD",
        malappuram: "MLP",
        palakkad: "PLK",
        thrissur: "TSR",
        ernakulam: "EKM",
        idukki: "IDK",
        kottayam: "KTM",
        alappuzha: "ALP",
        pathanamthitta: "PTA",
        kollam: "KLM",
        thiruvananthapuram: "TVM",
        trivandrum: "TVM",
      };
      const cleanName = String(name).trim().toLowerCase();
      return lookup[cleanName] || name.substring(0, 3).toUpperCase();
    },
    getAgeingPercentage(count) {
      const maxVal = Math.max(
        this.summary.ageing.under2h || 0,
        this.summary.ageing.from2to6h || 0,
        this.summary.ageing.from6to12h || 0,
        this.summary.ageing.over12h || 0,
        1
      );
      return Math.round((count / maxVal) * 100);
    },
    getDivisionPercentage(count) {
      if (!this.summary.divisionWiseIncidents || this.summary.divisionWiseIncidents.length === 0) return 0;
      const maxCount = this.summary.divisionWiseIncidents[0].count;
      return maxCount > 0 ? Math.round((count / maxCount) * 100) : 0;
    },
    getCategoryPercentage(count) {
      if (!this.summary.categoryWiseCalls || this.summary.categoryWiseCalls.length === 0) return 0;
      const maxCount = this.summary.categoryWiseCalls[0].count;
      return maxCount > 0 ? Math.round((count / maxCount) * 100) : 0;
    },
    getAnimalPercentage(count) {
      if (!this.summary.animalWiseIncidents || this.summary.animalWiseIncidents.length === 0) return 0;
      const maxCount = this.summary.animalWiseIncidents[0].count;
      return maxCount > 0 ? Math.round((count / maxCount) * 100) : 0;
    },
    getCategoryColor(index) {
      const colors = ["#2e5a38", "#ffb74d", "#8fae95", "#ff8a65"];
      return colors[index % colors.length];
    },
    getAnimalColor(index) {
      const colors = ["#8fae95", "#ffb74d", "#ff8a65", "#c62828"];
      return colors[index % colors.length];
    },
    onDateChange(value) {
      // The picker shows the opening day; the API is keyed by the closing day.
      this.selectedDate = shiftIstDays(value, 1);
      this.dateMenu = false;
      this.loadDashboard({ showLoader: true });
    },
    async loadDashboard({ showLoader = false } = {}) {
      if (showLoader || !this.hasLoadedOnce) {
        this.appLoading = true;
      }

      if (!this.hasLoadedOnce) {
        this.serverError = false;
      }

      try {
        const [summaryRes, districtShapeRes] = await Promise.all([
          axios({
            method: "GET",
            url: "/minister/dashboard/summary",
            headers: { "x-auth-token": localStorage.getItem("token") },
            params: {
              date: this.selectedDate,
              type: this.effectiveType,
            },
          }),
          axios({
            method: "GET",
            url: "/alldata/formap/",
            headers: { "x-auth-token": localStorage.getItem("token") },
          }),
        ]);

        const summaryData = summaryRes.data.data || {};
        if (!summaryData.snakeSummary) {
          summaryData.snakeSummary = {
            totalCalls: 0,
            venomousCount: 0,
            nonVenomousCount: 0,
            rescuedCount: 0,
            releasedCount: 0,
            humanBitesCount: 0,
            animalBitesCount: 0,
            rrtDispatched: 0,
          };
        }
        if (!summaryData.ageing) {
          summaryData.ageing = { under2h: 0, from2to6h: 0, from6to12h: 0, over12h: 0, rrt: 0, critical: 0 };
        }
        if (!summaryData.responsePerformance) {
          summaryData.responsePerformance = { avgResponse: "N/A", longestPending: "N/A", escalatedCount: 0 };
        }

        this.summary = { ...this.summary, ...summaryData };
        // previousDayLabel is computed from the selected day - nothing to set here.
        this.districtGeoJson = districtShapeRes.data.data || null;

        this.buildDistrictMap();

        // Update Donut Chart
        this.statusChartOptions.series[0].data = [
          { name: "Resolved", y: this.summary.incidentsResolved || 0, color: "#2e5a38" },
          { name: "Pending", y: this.summary.incidentsPending || 0, color: "#ff8a65" },
        ];
        this.statusChartOptions.title.text = `
          <div style="text-align: center; line-height: 1.2;">
            <div style="font-size: 24px; font-weight: 800; color: #173824; font-family: Poppins, sans-serif;">${this.summary.incidentsReported || 0}</div>
            <div style="font-size: 11px; color: #718171; font-weight: 500; font-family: Poppins, sans-serif;">Total</div>
          </div>
        `;

        this.fitDistrictMap();
        this.hasLoadedOnce = true;
        this.serverError = false;
      } catch (error) {
        console.error(error);
        if (!this.hasLoadedOnce) {
          this.serverError = true;
        }
      } finally {
        if (showLoader || !this.hasLoadedOnce) {
          this.appLoading = false;
        }
      }
    },
    buildDistrictMap() {
      const districtMap = {};
      (this.summary.districtWiseIncidents || []).forEach((item) => {
        if (item.name) {
          const lookupKey = item.name.toLowerCase().trim();
          districtMap[lookupKey] = item.count;
        }
      });
      this.districtChartMap = districtMap;
    },
    fitDistrictMap() {
      this.$nextTick(() => {
        if (!this.$refs.districtMap || !this.$refs.districtMap.mapObject || !this.districtGeoJson) {
          return;
        }

        const map = this.$refs.districtMap.mapObject;
        const bounds = L.geoJSON(this.districtGeoJson).getBounds();
        if (bounds.isValid()) {
          map.invalidateSize();
          map.fitBounds(bounds.pad(0.02));
        }
      });
    },
  },
};
</script>

<style scoped>
.minister-dashboard {
  min-height: calc(100vh - 56px);
  display: flex;
  flex-direction: column;
  padding: 24px;
  background-color: #f4f7f5;
  font-family: 'Poppins', sans-serif;
  color: #173824;
}

.minister-dashboard__grid-wrapper {
  display: grid;
  grid-template-columns: minmax(0, 2.2fr) minmax(0, 1fr);
  gap: 16px;
  width: 100%;
  align-items: start;
}

.minister-dashboard__main-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.minister-dashboard__side-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* Situation Overview Bar */
.situation-overview-bar {
  display: flex;
  flex-direction: column;
  margin-top: 4px;
  margin-bottom: 4px;
}

.overview-bar-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.overview-header {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  color: #697b69;
  letter-spacing: 0.08em;
}

.overview-body {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
}

.overview-date {
  font-size: 24px;
  font-weight: 800;
  color: #173824;
}

.overview-badge {
  background: rgba(143, 174, 149, 0.2);
  color: #2e5a38;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 12px;
}

.overview-filter {
  min-width: 220px;
}

.date-filter-input {
  background: #ffffff;
  border-radius: 12px;
}

/* Stats Row */
.minister-dashboard__stats {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
  flex-shrink: 0;
}

.stat-card {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.01);
  padding: 12px 16px;
  min-height: 76px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.stat-card--clickable {
  cursor: pointer;
}

.stat-card--clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(18, 42, 24, 0.04);
}

.stat-card__label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #697b69;
  letter-spacing: 0.05em;
}

.stat-card__value {
  font-size: 28px;
  font-weight: 800;
  color: #173824;
  line-height: 1.1;
}

.text-orange {
  color: #ff8a65 !important;
}

.left-column {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.left-column__row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;
}

.right-column {
  display: flex;
  flex-direction: column;
}

/* Panels */
.panel {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.01);
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: all 0.2s ease;
}

.panel__head {
  margin-bottom: 10px;
}

.panel__title {
  font-size: 15px;
  font-weight: 750;
  color: #173824;
  line-height: 1.2;
}

.panel__subtitle {
  margin-top: 2px;
  font-size: 10px;
  color: #718171;
  font-weight: 500;
}

.panel__chart-container {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Custom Legend for Status */
.custom-legend {
  width: 100%;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-box {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  display: inline-block;
}

.legend-text-label {
  font-size: 11px;
  font-weight: 600;
  color: #173824;
}

/* List views inside cards */
.list-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ageing-item,
.division-item,
.category-item,
.animal-item {
  width: 100%;
}

.ageing-label {
  font-size: 11px;
  font-weight: 600;
  color: #173824;
  min-width: 54px;
}

.division-label {
  font-size: 11px;
  font-weight: 600;
  color: #173824;
  min-width: 100px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.category-label {
  font-size: 11px;
  font-weight: 600;
  color: #173824;
  min-width: 130px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.animal-label {
  font-size: 11px;
  font-weight: 600;
  color: #173824;
  min-width: 80px;
}

.ageing-value,
.division-value,
.category-value,
.animal-value {
  font-size: 11px;
  font-weight: 700;
  color: #173824;
  min-width: 24px;
  text-align: right;
}

.border-top {
  border-top: 1px solid rgba(34, 64, 35, 0.06);
}

.gap-2 {
  gap: 8px;
}

.gap-4 {
  gap: 16px;
}

/* Priority Flags */
.priority-badge-mockup {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 12px;
  color: #173824;
}

.priority-badge-mockup--rrt {
  background-color: #fbe9e7;
  color: #d84315;
}

.priority-badge-mockup--critical {
  background-color: #ffebee;
  color: #c62828;
}

/* Performance Metrics */
.perf-list {
  display: flex;
  flex-direction: column;
}

.perf-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid rgba(34, 64, 35, 0.05);
}

.perf-row:last-child {
  border-bottom: 0;
}

.perf-label {
  font-size: 11px;
  font-weight: 600;
  color: #718171;
}

.perf-value {
  font-size: 13px;
  font-weight: 750;
  color: #173824;
}

.grid-span-2 {
  grid-column: span 2;
}

/* Casualty & Damage Grid */
.damage-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

@media (max-width: 1399px) {
  .damage-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
  }
}

.damage-card--clear {
  background: #f1f8f2;
  border-color: rgba(46, 125, 50, 0.18);
}
.damage-card__note {
  font-size: 9.5px;
  color: #6f7d70;
  line-height: 1.2;
  margin-top: 2px;
}
.text-green-bold {
  color: #2e7d32;
  font-weight: 700;
}

.damage-card {
  background: #f8fafc;
  border: 1px solid rgba(34, 64, 35, 0.08);
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  transition: border-color 0.2s ease, background 0.2s ease;
}
.damage-card:hover {
  background: #ffffff;
  border-color: rgba(34, 64, 35, 0.18);
}
/* Tile icons get the same tinted-chip treatment as the case cards. */
.damage-card__icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #ebf1ec;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.damage-card:hover {
  background: #ffffff;
  border-color: rgba(46, 90, 56, 0.25);
  box-shadow: 0 4px 10px rgba(18, 42, 24, 0.06);
}

.damage-card--highlight {
  background: rgba(143, 174, 149, 0.15);
  border-color: rgba(46, 90, 56, 0.3);
}

.damage-card__icon {
  flex-shrink: 0;
}
.damage-card__icon--danger {
  background: #fdecea;
}
.damage-card__icon--warn {
  background: #fdf0e6;
}
.damage-card__icon--loss {
  background: #fdf6db;
}
.damage-card__icon--good {
  background: #e8f5eb;
}

.damage-card__info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.damage-card__label {
  font-size: 11px;
  font-weight: 600;
  color: #5f7d6b;
  text-transform: none;
  letter-spacing: 0.01em;
  line-height: 1.25;
  white-space: normal;
  word-break: break-word;
}

.damage-card__value {
  font-size: 19px;
  font-weight: 800;
  color: #173824;
  line-height: 1.1;
  margin-top: 2px;
}

.damage-card__value--sm {
  font-size: 10.5px;
  line-height: 1.15;
  white-space: normal;
  word-break: break-word;
}

.text-amber-bold {
  color: #d84315 !important;
  font-weight: 800;
}

.text-red-bold {
  color: #c62828 !important;
  font-weight: 800;
}

/* Map specific styling */
.panel--map {
  display: flex;
  flex-direction: column;
}

.district-map {
  height: 440px;
  border-radius: 12px;
  overflow: hidden;
  background: radial-gradient(circle at top, #f6fbf3 0%, #e5efe0 58%, #dbe7d5 100%);
  border: 2px solid #000000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.district-map__canvas {
  width: 100%;
  height: 100%;
}

/* Map Legend */
.map-legend {
  background: #ffffff;
  border-radius: 12px;
  padding: 12px;
  margin-top: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.map-legend__title {
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  color: #173824;
  margin-bottom: 8px;
  letter-spacing: 0.02em;
}

.map-legend__grid {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.map-legend__item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-dot {
  width: 14px;
  height: 14px;
  border-radius: 4px;
  display: inline-block;
}

.legend-text {
  font-size: 10px;
  font-weight: 600;
  color: #546654;
}

/* Leaflet Overrides */
::v-deep .leaflet-container {
  background: transparent;
}

::v-deep .leaflet-control-zoom {
  border: 0 !important;
  box-shadow: 0 4px 12px rgba(23, 56, 36, 0.08) !important;
  margin: 10px !important;
}

::v-deep .leaflet-control-zoom a {
  width: 28px !important;
  height: 28px !important;
  line-height: 28px !important;
  background: #ffffff !important;
  color: #183824 !important;
  border: 1px solid #dce7d7 !important;
}

::v-deep .leaflet-control-zoom a:hover {
  background: #f4f8f1 !important;
}

::v-deep .district-marker-label {
  background: #173824;
  border: 1.5px solid #ffffff;
  border-radius: 8px;
  padding: 4px 6px;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  white-space: nowrap;
  transform: translate(-50%, -50%);
  min-width: 38px;
  line-height: 1.1;
}

::v-deep .district-marker-label__name {
  font-size: 8px;
  font-weight: 700;
  color: #ffffff;
  text-transform: uppercase;
}

::v-deep .district-marker-label__count {
  font-size: 9px;
  font-weight: 800;
  color: #ffffff;
  margin-top: 1px;
}

/* Responsive adjustments - step down gradually rather than collapsing
   straight from three columns to one at a single 1400px cliff. */
@media (max-width: 1600px) {
  .minister-dashboard__stats {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
  .damage-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

/* Map moves below the panels; left content gets the full width. */
@media (max-width: 1400px) {
  .minister-dashboard__grid-wrapper {
    grid-template-columns: minmax(0, 1fr);
  }
  .left-column__row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .grid-span-2 {
    grid-column: span 2;
  }
  .district-map {
    height: 460px;
  }
}

@media (max-width: 1100px) {
  .minister-dashboard {
    padding: 16px;
  }
  .minister-dashboard__stats {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .damage-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 860px) {
  .left-column__row {
    grid-template-columns: minmax(0, 1fr);
  }
  .grid-span-2 {
    grid-column: span 1;
  }
  .minister-dashboard__stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .damage-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .district-map {
    height: 340px;
  }
}

@media (max-width: 768px) {
  .overview-bar-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .overview-filter {
    width: 100%;
    min-width: 0;
  }
}

@media (max-width: 520px) {
  .minister-dashboard {
    padding: 12px;
  }
  .minister-dashboard__stats,
  .damage-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
